import { FC, useState } from 'react';
import { Chart, ChartBreakdownRequest, KaizenChartForHooks, RequestVariables } from '@kaizenplatform/kaizen-chartjs';
import ChartWrapper from '../ChartBase/ChartWrapper';
import ChartContainer from '../ChartBase/ChartContainer';
import { CardHeader, Card, Divider } from '@material-ui/core';
import {
  GetChartQueryVariables,
  GetChartDocument,
  GetChartQueryResult,
  ChartScopeInput,
  GetCustomReportDataQueryResult,
  GetCustomReportDataQueryVariables,
  GetCustomReportDataDocument,
} from '../../../../generated/graphql';
import { useQuery } from '@apollo/client';
import GlobalPeriod from '../../../../entity/GlobalPeriod';
import styled from '@emotion/styled';

type Props = {
  chartId?: string;
  customReportChartSetting: GlobalPeriod;
  customReportLoading: boolean;
  globalBreakdownRequests?: ChartBreakdownRequest[];
};

const CustomReportChart: FC<Props> = ({
  chartId,
  customReportChartSetting,
  customReportLoading,
  globalBreakdownRequests,
}) => {
  const [chartTitle, setChartTitle] = useState<string>();
  const scope: ChartScopeInput = {
    from: customReportChartSetting.from.toISO(),
    to: customReportChartSetting.to.toISO(),
  };

  const variablesForFrequency = {
    id: chartId ? chartId : '',
    scope: scope,
  };

  const {
    data: customReportData,
    loading: frequencyLoading,
    //  error: frequencyError
  } = useQuery<GetCustomReportDataQueryResult, GetCustomReportDataQueryVariables>(GetCustomReportDataDocument, {
    skip: !chartId,
    variables: variablesForFrequency,
    context: {
      clientName: 'dataplatform',
    },
  });

  const initialFrequency =
    customReportData && (customReportData as any).chart?.frequencies
      ? (customReportData as any).chart?.frequencies.includes('WEEKLY')
        ? 'WEEKLY'
        : (customReportData as any).chart?.frequencies[0]
      : undefined;

  const scopeForChart = {
    from: customReportChartSetting.from.toISO(),
    to: customReportChartSetting.to.toISO(),
    frequency: initialFrequency,
  };

  /* eslint @typescript-eslint/no-array-constructor:0 */
  const breakdowns = new Array();

  if (globalBreakdownRequests) {
    globalBreakdownRequests.map((item) => {
      breakdowns?.push(
        new ChartBreakdownRequest({
          breakdown: item.breakdown,
          filter: item.filter,
        }),
      );
    });
  }

  const variablesForChart = {
    id: chartId ? chartId : '',
    scope: scopeForChart,
    breakdowns: breakdowns,
  };

  const {
    data: chartData,
    loading: chartLoading,
    // error: chartError,
    refetch: refetchChart,
  } = useQuery<GetChartQueryResult, GetChartQueryVariables>(GetChartDocument, {
    skip: !chartId && !customReportData,
    variables: variablesForChart,
    notifyOnNetworkStatusChange: true,
    context: {
      clientName: 'dataplatform',
    },
  });

  const refetch = (variables: RequestVariables) => {
    const _variables = { ...variables };
    if (globalBreakdownRequests && _variables.breakdowns) {
      globalBreakdownRequests.map((item) => {
        if (_variables.breakdowns)
          _variables.breakdowns.push(
            new ChartBreakdownRequest({
              breakdown: item.breakdown,
              filter: item.filter,
            }),
          );
      });
    }

    refetchChart(_variables);
  };

  if (chartTitle === undefined && customReportData) {
    setChartTitle((customReportData as any).chart !== undefined ? (customReportData as any).chart.name : '');
  }

  return (
    <Card>
      {/* <CardHeader title="hoge" /> */}
      <ChartTitle title={chartTitle} />
      <Divider />
      <ChartWrapper>
        <ChartContainer>
          <KaizenChartForHooks
            chartData={chartData ? ((chartData as any).chart as Chart) : undefined}
            identifier={{ id: chartId ? chartId : '' }}
            scope={{
              from: customReportChartSetting.from.toISO(),
              to: customReportChartSetting.to.toISO(),
              frequency: initialFrequency,
            }}
            loading={customReportLoading || frequencyLoading || chartLoading}
            refetch={refetch}
            frequencies={(chartData as any) ? ((chartData as any).chart as Chart).frequencies : undefined}
          />
        </ChartContainer>
      </ChartWrapper>
    </Card>
  );
};

const ChartTitle = styled(CardHeader)({
  height: '52px',
});

export default CustomReportChart;
